<template>
  <div class="source-viewer" @click="handleContainerClick">
    <div v-if="isDropdownOpen" class="dropdown-overlay" @click="isDropdownOpen = false"></div>
    <div v-if="previewSource" class="viewer-content">
      <!-- 视频已移动到父组件 -->
      
      <div class="info-area">
        <div class="meta-info" v-if="videoMetadata.shooting_time" style="display: none;">
            拍摄时间: {{ videoMetadata.shooting_time }}
        </div>
        
      <div class="actions-container">
           <div class="control-row top-control-row">
               <div class="influencer-container">
                    <span class="tag-influencer" v-if="influencerName" :title="previewSource.author_id ? 'ID: ' + previewSource.author_id : ''">
                        <i class="mdi mdi-account-circle-outline"></i> {{ influencerName }}
                    </span>
               </div>
               
               <div class="right-controls">
                    <div class="nav-controls">
                        <button class="nav-btn" @click="jumpToPrevMatch" title="上一个匹配">
                            <i class="mdi mdi-chevron-up"></i>
                        </button>
                        <div class="match-counter">
                            {{ matches.length > 0 ? currentMatchDisplayIndex : '-' }}/{{ matches.length || 1 }}
                        </div>
                        <button class="nav-btn" @click="jumpToNextMatch" title="下一个匹配">
                            <i class="mdi mdi-chevron-down"></i>
                        </button>
                    </div>

                    <div v-if="!isEditingTime" class="time-display editable-time" title="点击编辑时间跳转 (支持 MM:SS, 秒数或完整时间)" @click="startEditingTime">
                        {{ currentDisplayTime || '--:--:--' }}
                    </div>
                    <input 
                        v-else
                        class="time-display-input"
                        v-model="editableTimeStr"
                        @blur="handleTimeSeek"
                        @keyup.enter="handleTimeSeek"
                        @keyup.esc="resetTimeDisplay"
                        ref="timeInput"
                    />
                    <button class="nav-btn locate-btn" @click="handleLocate" title="定位到当前播放位置">
                        <i class="mdi mdi-crosshairs-gps"></i>
                    </button>
                    <button class="nav-btn help-btn-inline" @click.stop="showHelp = true" title="文稿视图帮助">
                        <i class="mdi mdi-help-circle-outline"></i>
                    </button>
               </div>
           </div>

           <div class="control-row">
               <div class="product-placeholder">
                   <!-- 热点视频关联商品显示 -->
                   <div v-if="previewSource.is_hot_video && previewSource.product_name" class="hot-product-info" :title="previewSource.product_name">
                       <span class="color-swatch" :style="{ backgroundColor: '#FFF3E0' }"></span>
                       <span class="product-label">关联商品:</span>
                       <span class="product-value">{{ previewSource.product_name }}</span>
                   </div>

                   <div class="custom-select-container" v-else-if="commodityList.length > 0">
                       <div class="custom-select-trigger" @click="isDropdownOpen = !isDropdownOpen">
                           <div v-if="!selectedCommodity" class="select-placeholder">-- 未在讲解商品 --</div>
                           <div v-else class="selected-item-display">
                               <div class="item-name">
                                   <span class="color-swatch" :style="{ backgroundColor: getCommodityColor(selectedCommodity['商品名称']) }"></span>
                                   {{ selectedCommodity['商品名称'] }}
                               </div>
                           </div>
                           <i class="mdi mdi-chevron-down"></i>
                       </div>
                       <div class="custom-select-dropdown" v-show="isDropdownOpen">
                           <div class="custom-option" @click="selectCommodityItem(null)">
                               -- 未在讲解商品 --
                           </div>
                           <div class="custom-option" v-for="(item, idx) in commodityList" :key="idx" @click="selectCommodityItem(item)" :class="{ active: selectedCommodity === item }">
                               <div class="item-name">
                                   <span class="color-swatch" :style="{ backgroundColor: getCommodityColor(item['商品名称']) }"></span>
                                   {{ item['商品名称'] }}
                               </div>
                               <div class="item-details">时长: {{ Number(item['讲解时长（分钟）'] || 0).toFixed(1) }}分 | 销量: {{ item['全网销量'] || '未知' }}</div>
                           </div>
                       </div>
                   </div>
                   <span v-else-if="loadingCommodities" class="hint-text">加载商品中...</span>
                   <span v-else class="hint-text">暂无商品讲解记录</span>
                   
                   <button v-if="selectedCommodity" class="select-segment-btn" @click="selectCommoditySegment" title="全选讲解片段">
                       <span class="btn-main-text">全选讲解片段</span>
                       <span class="btn-sub-text">{{ Number(selectedCommodity['讲解时长（分钟）'] || 0).toFixed(1) }}分 | 销量: {{ selectedCommodity['全网销量'] || '未知' }}</span>
                   </button>
               </div>
               <button class="add-btn" @click="addToPool" :disabled="!hasSelection">
                   <span class="btn-main-text">添加到剪辑池</span>
                   <span class="btn-sub-text" v-if="hasSelection">({{ selectedDuration.toFixed(1) }}s)</span>
               </button>
           </div>
      </div>
      </div>
      
      <div class="text-selection-area" @mousedown="handleUserInteraction" @wheel="handleUserInteraction" @touchmove="handleUserInteraction">
        <div v-if="loadingTranscript" class="loading-hint">字幕加载中...</div>
        <template v-else-if="wordDf && wordDf.length > 0">
            <div class="video-nav-inline" v-if="previewSource && (previewSource.video_id || previewSource.id)">
                <div class="nav-item" @click="handleNavigate('prev', true)">
                    <i class="mdi mdi-arrow-left"></i>
                    <span>上一段</span>
                </div>
                <div class="nav-info">同场直播</div>
                <div class="nav-item" @click="handleNavigate('next')">
                    <span>下一段</span>
                    <i class="mdi mdi-arrow-right"></i>
                </div>
            </div>
            
            <div class="transcript-content">
                <span 
                    v-for="(word, index) in wordDf" 
                    :key="index"
                    class="word-item"
                    :class="{ 
                        'playing': index === currentWordIdx,
                        'matched-text': textMatchIndices.has(index),
                        'matched-highlight': highlightIndices.has(index),
                        'matched-time': timeMatchIndices.has(index) && !wordCommodityColors[index],
                        'user-selected': isWordSelected(index)
                    }"
                    :style="getWordStyle(index)"
                    :data-index="index"
                    @click="handleWordClick(index)"
                >{{ getWordText(word) }}</span>
            </div>
            
            <div class="video-nav-inline" v-if="previewSource && (previewSource.video_id || previewSource.id)">
                <div class="nav-item" @click="handleNavigate('prev')">
                    <i class="mdi mdi-arrow-left"></i>
                    <span>上一段</span>
                </div>
                <div class="nav-info">同场直播</div>
                <div class="nav-item" @click="handleNavigate('next')">
                    <span>下一段</span>
                    <i class="mdi mdi-arrow-right"></i>
                </div>
            </div>
        </template>
        <p v-else class="transcript-hint">暂无字幕信息</p>
      </div>
    </div>
    
    <div v-else class="placeholder-full">
      <div class="empty-state">
        <i class="mdi mdi-play-box-outline"></i>
        <p>请从左侧列表选择素材</p>调用的接口是服务端这块的路接口。
      </div>
    </div>
    <Teleport to="body">
      <div v-if="showHelp" class="help-overlay" @click.self="showHelp = false">
        <div class="help-modal">
          <div class="help-header">
            <h3>文稿视图功能指南</h3>
            <button class="close-btn" @click="showHelp = false">×</button>
          </div>
          <div class="help-content">
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-chevron-up"></i></div>
              <div class="step-info">
                <h4>快速导航</h4>
                <p>使用上下箭头 <i class="mdi mdi-chevron-up"></i> <i class="mdi mdi-chevron-down"></i> 可以在搜索结果或高亮片段之间快速跳转。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-format-color-highlight"></i></div>
              <div class="step-info">
                <h4>高亮显示 (红字/黄底)</h4>
                <p><strong>黄底</strong>：表示搜索匹配的关键词或选中的高亮片段范围。<br><strong>红字/红底</strong>：表示当前正在播放的单词。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-cursor-text"></i></div>
              <div class="step-info">
                <h4>文本选择</h4>
                <p>鼠标圈选 -> 按 A 键 -> 添加到剪辑池</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-keyboard"></i></div>
              <div class="step-info">
                <h4>快捷键</h4>
                <p><strong>空格</strong>：播放/暂停<br><strong>A</strong>：将选中片段添加到剪辑池</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-crosshairs-gps"></i></div>
              <div class="step-info">
                <h4>定位</h4>
                <p>如果浏览时迷失了位置，点击定位按钮 <i class="mdi mdi-crosshairs-gps"></i> 可立即滚动回当前播放进度的位置。</p>
              </div>
            </div>
          </div>
          <div class="help-footer">
            <button class="primary-btn" @click="showHelp = false">知道了</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { computed, ref, shallowRef, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { get_video_material_info, query_outter_commodity_explanation, get_adjacent_transcription } from '@/services/api'

export default {
  name: 'SourceViewer',
  props: {
    currentTime: {
      type: Number,
      default: 0
    }
  },
  emits: ['request-seek'],
  setup(props, { emit }) {
    const store = useStore()
    const previewSource = computed(() => store.state.multiClipper.previewSource)
    const activePanel = computed(() => store.state.multiClipper.activePanel)
    const highlightRanges = computed(() => store.state.multiClipper.highlightRanges)

    const wordDf = shallowRef([])
    const showHelp = ref(false)
    const loadingTranscript = ref(false)
    const commodityList = ref([])
    const selectedCommodity = ref(null)
    const loadingCommodities = ref(false)
    const isUserSelectingCommodity = ref(false)
    const isDropdownOpen = ref(false)
    const selectionStartIdx = ref(null)
    const selectionEndIdx = ref(null)
    // currentWordIdx 严格用于高亮显示（如果在间隙中可以为 -1）
    const currentWordIdx = ref(-1)
    // lastSearchIdx 是用于优化的游标，记录上次的位置
    let lastSearchIdx = 0

    const autoScroll = ref(true)
    const scrollAfterLoad = ref(null)
    // 未来功能的占位符
    const matches = shallowRef([]) 
    const currentMatchIdx = ref(-1)
    
    // 高亮集合 (Use shallowRef for sets if possible, but keeping as ref since they are replaced entirely)
    const textMatchIndices = shallowRef(new Set())
    const highlightIndices = shallowRef(new Set())
    // 不同组（普通组/溯源/AI 参考源）的高亮颜色区分：index -> color
    const highlightColorMap = shallowRef(new Map())
    const timeMatchIndices = shallowRef(new Set())
    
    // 存储每个词对应的商品专属底色
    const wordCommodityColors = shallowRef([])
    const commodityColorMap = ref(new Map())

    const updateCommodityColors = () => {
        if (!videoMetadata.value.shooting_time || commodityList.value.length === 0 || wordDf.value.length === 0) {
            wordCommodityColors.value = []
            commodityColorMap.value = new Map()
            return
        }
        
        // 浅色背景调色板，用于不同商品
        const colors = [
            '#E8F5E9', '#FCE4EC', '#FFF3E0', '#F3E5F5', 
            '#E0F7FA', '#FBE9E7', '#F9FBE7', '#E8EAF6', 
            '#E0F2F1', '#FFF9C4', '#FFECB3'
        ]
        const colorMap = new Map()
        let colorIndex = 0
        
        commodityList.value.forEach(item => {
            const name = item['商品名称']
            if (!colorMap.has(name)) {
                colorMap.set(name, colors[colorIndex % colors.length])
                colorIndex++
            }
        })
        
        commodityColorMap.value = colorMap
        
        const shootingTimeMs = new Date(videoMetadata.value.shooting_time).getTime()
        const newColors = new Array(wordDf.value.length).fill(null)
        
        commodityList.value.forEach(item => {
            const explainStart = new Date(item['讲解开始时间']).getTime()
            const explainEnd = new Date(item['讲解结束时间']).getTime()
            const relativeStartMs = Math.max(0, explainStart - shootingTimeMs)
            const relativeEndMs = explainEnd - shootingTimeMs
            const color = colorMap.get(item['商品名称'])
            
            let sIdx = wordDf.value.findIndex(w => w.end_time > relativeStartMs)
            if (sIdx !== -1) {
                for (let i = sIdx; i < wordDf.value.length; i++) {
                    if (wordDf.value[i].start_time < relativeEndMs) {
                        newColors[i] = color
                    } else {
                        break
                    }
                }
            }
        })
        
        wordCommodityColors.value = newColors
    }

    const getCommodityColor = (name) => {
        return commodityColorMap.value.get(name) || 'transparent'
    }

    watch([commodityList, wordDf], () => {
        updateCommodityColors()
    })

    const getWordStyle = (index) => {
        // 当前播放的词样式优先级最高
        if (index === currentWordIdx.value) return {}
        // 不同组的高亮颜色区分（优先级高于默认高亮底色）
        const hlColor = highlightColorMap.value.get(index)
        if (hlColor) {
            return { backgroundColor: hlColor, borderBottom: `2px solid ${hlColor}` }
        }
        // 搜索高亮和文本匹配颜色保留（不被商品颜色覆盖）
        if (textMatchIndices.value.has(index) || highlightIndices.value.has(index)) return {}
        
        if (wordCommodityColors.value[index]) {
            return { backgroundColor: wordCommodityColors.value[index] }
        }
        return {}
    }

    const videoMetadata = ref({})
    const initialJumpDone = ref(false)
    const editableTimeStr = ref('')
    const isEditingTime = ref(false)

    const influencerName = computed(() => {
        if (!previewSource.value) return ''
        return previewSource.value['达人昵称'] || 
               previewSource.value.talent_nickname || 
               previewSource.value.author || 
               previewSource.value.author_name ||
               previewSource.value.influencer_name ||
               ''
    })

    const formatDateTimeStr = (timeStr) => {
      if (!timeStr) return ''
      try {
        const date = new Date(timeStr)
        if (isNaN(date.getTime())) return timeStr
        
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        
        return `${year}-${month}-${day} ${hours}:${minutes}`
      } catch (e) {
        return timeStr
      }
    }

    const formatDateTime = (date) => {
         const pad = n => n < 10 ? '0' + n : n
         const Y = date.getFullYear()
         const M = pad(date.getMonth() + 1)
         const D = pad(date.getDate())
         const h = pad(date.getHours())
         const m = pad(date.getMinutes())
         const s = pad(date.getSeconds())
         return `${Y}-${M}-${D} ${h}:${m}:${s}`
    }

    const formatYMD = (date) => {
        const pad = n => n < 10 ? '0' + n : n
        const Y = date.getFullYear()
        const M = pad(date.getMonth() + 1)
        const D = pad(date.getDate())
        return `${Y}-${M}-${D}`
    }

    const fetchCommodities = async (payload) => {
        // console.log('[SourceViewer] fetchCommodities payload:', payload)
        
        let durationSec = payload.duration
        if (!durationSec && payload.start_ts && payload.end_ts) {
            // Helper to convert "HH:MM:SS" to seconds
            const toSeconds = (ts) => {
                const parts = ts.split(':')
                if (parts.length === 3) return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2])
                return 0
            }
            durationSec = toSeconds(payload.end_ts) - toSeconds(payload.start_ts)
        }

        if (!payload || !payload.influencer_id || !payload.shooting_time || !durationSec) {
            // console.log('[SourceViewer] fetchCommodities aborted due to missing fields. influencer_id:', payload?.influencer_id, 'shooting_time:', payload?.shooting_time, 'duration:', durationSec)
            return
        }
        
        loadingCommodities.value = true
        commodityList.value = []
        try {
            const startDate = new Date(payload.shooting_time)
            const startStr = formatYMD(startDate)
            const endDate = new Date(startDate.getTime() + durationSec * 1000)
            endDate.setDate(endDate.getDate() + 1)
            const endStr = formatYMD(endDate)
            
            const res = await query_outter_commodity_explanation({
                kol_list: payload.influencer_id,
                start_date: startStr,
                end_date: endStr
            })
            
            let data = res
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data)
                } catch(e) {
                    console.error('Failed to parse commodity explanation data', e)
                    data = []
                }
            } else if (res && res.data) {
                data = res.data.data ? res.data.data : res.data
                if (typeof data === 'string') {
                    try {
                        data = JSON.parse(data)
                    } catch(e) {
                        data = []
                    }
                }
            }
            if (!data) data = []
            
            const shootingTimeMs = startDate.getTime()
            const videoEndMs = shootingTimeMs + durationSec * 1000
            
            if (Array.isArray(data)) {
                const validItems = data.filter(item => {
                    const explainStart = new Date(item['讲解开始时间']).getTime()
                    const explainEnd = new Date(item['讲解结束时间']).getTime()
                    return (explainStart <= videoEndMs && explainEnd >= shootingTimeMs)
                })
                
                validItems.sort((a, b) => {
                    return new Date(a['讲解开始时间']).getTime() - new Date(b['讲解开始时间']).getTime()
                })
                
                commodityList.value = validItems
            }
        } catch (e) {
            console.error("Failed to fetch commodities", e)
        } finally {
            loadingCommodities.value = false
        }
    }

    const handleCommodityChange = () => {
        isUserSelectingCommodity.value = true
        setTimeout(() => { isUserSelectingCommodity.value = false }, 1000)
        
        const item = selectedCommodity.value
        if (item && videoMetadata.value.shooting_time) {
            const shootingTimeMs = new Date(videoMetadata.value.shooting_time).getTime()
            const explainStart = new Date(item['讲解开始时间']).getTime()
            
            const relativeStartMs = Math.max(0, explainStart - shootingTimeMs)
            
            emit('request-seek', relativeStartMs / 1000)
            
            let sIdx = -1
            for (let i = 0; i < wordDf.value.length; i++) {
                const w = wordDf.value[i]
                if (w.end_time > relativeStartMs) {
                    sIdx = i
                    break
                }
            }
            
            if (sIdx !== -1) {
                nextTick(() => {
                    const container = document.querySelector('.transcript-content')
                    if (container) {
                        const startEl = container.querySelector(`.word-item[data-index="${sIdx}"]`)
                        if (startEl) {
                            startEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
                        }
                    }
                })
            }
        }
    }

    const selectCommodityItem = (item) => {
        selectedCommodity.value = item
        isDropdownOpen.value = false
        handleCommodityChange()
    }

    const selectCommoditySegment = () => {
        const item = selectedCommodity.value
        if (item && videoMetadata.value.shooting_time) {
            const shootingTimeMs = new Date(videoMetadata.value.shooting_time).getTime()
            const explainStart = new Date(item['讲解开始时间']).getTime()
            const explainEnd = new Date(item['讲解结束时间']).getTime()
            
            const relativeStartMs = Math.max(0, explainStart - shootingTimeMs)
            const relativeEndMs = explainEnd - shootingTimeMs
            
            let sIdx = -1
            let eIdx = -1
            for (let i = 0; i < wordDf.value.length; i++) {
                const w = wordDf.value[i]
                if (sIdx === -1 && w.end_time > relativeStartMs) {
                    sIdx = i
                }
                if (w.start_time < relativeEndMs) {
                    eIdx = i
                }
            }
            
            if (sIdx !== -1 && eIdx !== -1 && sIdx <= eIdx) {
                selectionStartIdx.value = sIdx
                selectionEndIdx.value = eIdx
                
                nextTick(() => {
                    const container = document.querySelector('.transcript-content')
                    if (container) {
                        const startEl = container.querySelector(`.word-item[data-index="${sIdx}"]`)
                        const endEl = container.querySelector(`.word-item[data-index="${eIdx}"]`)
                        if (startEl && endEl) {
                            const sel = window.getSelection()
                            sel.removeAllRanges()
                            const r = document.createRange()
                            r.setStartBefore(startEl)
                            r.setEndAfter(endEl)
                            sel.addRange(r)
                            startEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
                        }
                    }
                })
            } else {
                 selectionStartIdx.value = null
                 selectionEndIdx.value = null
            }
        }
    }

    const currentDisplayTime = computed(() => {
        if (!videoMetadata.value.shooting_time) return ''
        const shootTime = new Date(videoMetadata.value.shooting_time).getTime()
        if (isNaN(shootTime)) return videoMetadata.value.shooting_time
        
        // props.currentTime is in seconds
        const currentMs = shootTime + (props.currentTime * 1000)
        const date = new Date(currentMs)
        
        return formatDateTime(date)
    })

    // Sync editable time with current display time when not editing
    watch(currentDisplayTime, (newVal) => {
        if (!isEditingTime.value) {
            editableTimeStr.value = newVal
        }
    })

    watch(highlightRanges, () => {
        updateMatches()
    }, { deep: true })

    const timeInput = ref(null)

    const startEditingTime = () => {
        if (!videoMetadata.value.shooting_time) return
        isEditingTime.value = true
        nextTick(() => {
            if (timeInput.value) {
                timeInput.value.focus()
            }
        })
    }

    const parseUserInputTime = (inputStr, shootTimeMs, durSec) => {
        if (!inputStr) return NaN;
        inputStr = inputStr.trim();
        
        let targetTime = NaN;

        // 兼容斜杠/点号分隔的日期格式 (如 2026/8/16 12:24:08)，统一转换为横杠格式
        const normalizedStr = inputStr.replace(/[/.]/g, '-')

        // 1. Try parsing as full absolute time
        targetTime = new Date(normalizedStr).getTime();
        if (!isNaN(targetTime) && normalizedStr.includes('-') && normalizedStr.includes(':')) {
            return targetTime;
        }

        // 2. Check if it's a relative time format (MM:SS or HH:MM:SS)
        const timeFormatRegex = /^(\d+):(\d+)(?::(\d+))?$/;
        const match = inputStr.match(timeFormatRegex);
        if (match) {
            let hours = 0, minutes = 0, seconds = 0;
            if (match[3] !== undefined) {
                hours = parseInt(match[1], 10);
                minutes = parseInt(match[2], 10);
                seconds = parseInt(match[3], 10);
            } else {
                minutes = parseInt(match[1], 10);
                seconds = parseInt(match[2], 10);
            }
            const offsetSec = hours * 3600 + minutes * 60 + seconds;
            
            // If the input has hours and the calculated offset is way larger than duration,
            // it might be a time-of-day on the shooting date.
            if (match[3] !== undefined && durSec && offsetSec > durSec) {
                const shootDateStr = formatYMD(new Date(shootTimeMs));
                const absoluteAttempt = new Date(`${shootDateStr} ${inputStr}`).getTime();
                if (!isNaN(absoluteAttempt)) {
                    return absoluteAttempt;
                }
            }
            
            return shootTimeMs + offsetSec * 1000;
        }
        
        // 3. Check if it's pure seconds
        if (/^\d+(\.\d+)?$/.test(inputStr)) {
            return shootTimeMs + parseFloat(inputStr) * 1000;
        }

        // 4. Try parsing as time-of-day on the shooting date
        const shootDateStr = formatYMD(new Date(shootTimeMs));
        targetTime = new Date(`${shootDateStr} ${inputStr}`).getTime();
        if (!isNaN(targetTime)) {
            return targetTime;
        }

        return NaN;
    }

    const handleTimeSeek = () => {
        if (!isEditingTime.value) return
        
        if (!videoMetadata.value.shooting_time || !editableTimeStr.value) {
            resetTimeDisplay()
            return
        }
        
        const shootTime = new Date(videoMetadata.value.shooting_time).getTime()
        if (isNaN(shootTime)) {
            resetTimeDisplay()
            return
        }
        
        const durationSec = videoMetadata.value.duration || 0
        const targetTime = parseUserInputTime(editableTimeStr.value, shootTime, durationSec)
        
        if (isNaN(targetTime)) {
            console.warn('Invalid time format input')
            resetTimeDisplay()
            return
        }
        
        const diffMs = targetTime - shootTime
        const seekTime = Math.max(0, diffMs / 1000)
        
        emit('request-seek', seekTime)
        isEditingTime.value = false
    }

    const resetTimeDisplay = () => {
        // Delay slightly to allow click event on button to fire if that was the cause of blur
        setTimeout(() => {
             isEditingTime.value = false
             editableTimeStr.value = currentDisplayTime.value
        }, 200)
    }

    const currentMatchDisplayIndex = computed(() => {
        if (matches.value.length === 0) return 0
        // 如果 currentMatchIdx 是有效的，直接使用
        if (currentMatchIdx.value !== -1) return currentMatchIdx.value + 1
        
        // 否则尝试根据 currentWordIdx 计算
        // 找到最近的一个 match (start_time or wordIdx)
        // 这是一个近似值，用于显示
        return '-'
    })

    const setActivePanel = () => {
        if (activePanel.value !== 'source') {
            store.dispatch('multiClipper/setActivePanel', 'source')
        }
    }

    const handleContainerClick = () => {
        setActivePanel()
    }

    const scrollToWord = (index) => {
        if (index < 0) return
        // 使用 setTimeout 确保在数据更改后 DOM 已更新
        setTimeout(() => {
            const container = document.querySelector('.text-selection-area')
            // 如果存在多个实例，需要限定范围。使用 data-index
            const el = container ? container.querySelector(`.word-item[data-index="${index}"]`) : null
            if (el && container) {
                el.scrollIntoView({ block: 'center', behavior: 'smooth' })
            }
        }, 0)
    }

    const handleLocate = () => {
        autoScroll.value = true
        if (currentWordIdx.value !== -1) {
            scrollToWord(currentWordIdx.value)
        }
    }

    const handleUserInteraction = () => {
        // 如果用户与滚动区域交互，禁用自动滚动
        autoScroll.value = false
    }

    const handleNavigate = async (direction, fromBottom = false) => {
        if (!previewSource.value) return;
        
        const trans_id = previewSource.value.transcription_id || previewSource.value.video_id || previewSource.value.id;
        if (!trans_id) return;
        
        try {
            const res = await get_adjacent_transcription({
                transcription_id: trans_id,
                direction: direction,
                same_source: true
            });
            
            let data = res;
            if (res && res.data) {
                data = res.data.data ? res.data.data : res.data;
            }
            
            if (data && data.transcription_id) {
                const newSource = { 
                    ...previewSource.value, 
                    transcription_id: data.transcription_id,
                    video_id: data.transcription_id,
                    id: data.transcription_id,
                    _updateTs: Date.now()
                };
                
                newSource.matches = []; 
                store.dispatch('multiClipper/selectPreview', newSource);
                
                if (direction === 'prev' && fromBottom) {
                    scrollAfterLoad.value = 'bottom';
                } else {
                    scrollAfterLoad.value = 'top';
                }
            } else {
                alert(direction === 'prev' ? '没有上一个了' : '没有下一个了');
            }
        } catch (error) {
            console.error('获取相邻片段失败', error);
            alert('获取失败，请稍后重试');
        }
    };

    const jumpToPrevMatch = () => {
        if (matches.value.length === 0) return
        
        let prevIdx = -1
        
        // 查找当前播放位置 (currentWordIdx) 之前的最近匹配项
        if (currentWordIdx.value !== -1) {
            // 查找 wordIdx < currentWordIdx 的最后一个匹配项
            // 匹配项不一定按 wordIdx 排序（如果是搜索结果），但通常是。
            // 最好遍历并找到小于 currentWordIdx 的最大 wordIdx
            
            let bestIdx = -1
            let maxIdxVal = -1
            
            matches.value.forEach((m, i) => {
                if (m.wordIdx !== -1 && m.wordIdx < currentWordIdx.value) {
                    if (m.wordIdx > maxIdxVal) {
                        maxIdxVal = m.wordIdx
                        bestIdx = i
                    }
                }
            })
            
            if (bestIdx !== -1) prevIdx = bestIdx
            else {
                // 循环到最后一个匹配项
                // 找到具有最大 wordIdx 的匹配项
                let maxGlobal = -1
                matches.value.forEach((m, i) => {
                    if (m.wordIdx !== -1 && m.wordIdx > maxGlobal) {
                        maxGlobal = m.wordIdx
                        prevIdx = i
                    }
                })
            }
        } else {
             // 如果 currentWordIdx 为 -1（未播放或开始），跳转到最后一个匹配项？
             prevIdx = matches.value.length - 1
        }
        
        if (prevIdx !== -1) {
            currentMatchIdx.value = prevIdx
            const m = matches.value[prevIdx]
            if (m.wordIdx !== -1) {
                scrollToWord(m.wordIdx)
                const word = wordDf.value[m.wordIdx]
                if (word && word.start_time !== undefined) {
                    emit('request-seek', word.start_time / 1000)
                }
            }
        }
    }

    const jumpToNextMatch = () => {
        if (matches.value.length === 0) return
        
        let nextIdx = -1
        
        // 查找当前播放位置之后的最近匹配项
        if (currentWordIdx.value !== -1) {
            let bestIdx = -1
            let minIdxVal = Infinity
            
            matches.value.forEach((m, i) => {
                if (m.wordIdx !== -1 && m.wordIdx > currentWordIdx.value) {
                    if (m.wordIdx < minIdxVal) {
                        minIdxVal = m.wordIdx
                        bestIdx = i
                    }
                }
            })
            
            if (bestIdx !== -1) nextIdx = bestIdx
            else {
                // 循环到第一个匹配项
                let minGlobal = Infinity
                matches.value.forEach((m, i) => {
                    if (m.wordIdx !== -1 && m.wordIdx < minGlobal) {
                        minGlobal = m.wordIdx
                        nextIdx = i
                    }
                })
            }
        } else {
            nextIdx = 0
        }
        
        if (nextIdx !== -1) {
            currentMatchIdx.value = nextIdx
            const m = matches.value[nextIdx]
            if (m.wordIdx !== -1) {
                scrollToWord(m.wordIdx)
                const word = wordDf.value[m.wordIdx]
                if (word && word.start_time !== undefined) {
                    emit('request-seek', word.start_time / 1000)
                }
            }
        }
    }

    const updateMatches = () => {
        const localTextSet = new Set()
        const localHighlightSet = new Set()
        const localHighlightColors = new Map()
        const localTimeSet = new Set()

        if (!previewSource.value) {
            matches.value = []
            textMatchIndices.value = localTextSet
            highlightIndices.value = localHighlightSet
            highlightColorMap.value = localHighlightColors
            timeMatchIndices.value = localTimeSet
            return
        }
        
        if (wordDf.value.length === 0) {
            matches.value = []
            textMatchIndices.value = localTextSet
            highlightIndices.value = localHighlightSet
            highlightColorMap.value = localHighlightColors
            timeMatchIndices.value = localTimeSet
            return
        }
        
        // 构建全文映射以进行回退搜索
        let fullText = ''
        const charToWordMap = []
        wordDf.value.forEach((w, idx) => {
            const wText = getWordText(w)
            for(let i=0; i<wText.length; i++) {
                charToWordMap.push(idx)
            }
            fullText += wText
        })

        const newMatches = []
        const backendMatches = (previewSource.value && Array.isArray(previewSource.value.matches)) ? previewSource.value.matches : []
        const lowerFullText = fullText.toLowerCase()

        // Handle Highlight Ranges from Store (Group Selection)
        if (highlightRanges.value && highlightRanges.value.length > 0) {
             const currentId = previewSource.value.video_id || previewSource.value.id
             highlightRanges.value.forEach(range => {
                 // Loose comparison for ID safety
                 if (range.video_id && currentId && String(range.video_id) !== String(currentId)) return

                 const st = Number(range.start_time)
                 const et = Number(range.end_time)

                 let sIdx = wordDf.value.findIndex(w => w.end_time > st)
                 if (sIdx !== -1) {
                      let eIdx = sIdx
                      for(let i=sIdx; i<wordDf.value.length; i++) {
                          if (wordDf.value[i].start_time < et) {
                              eIdx = i
                          } else {
                              break
                          }
                      }
                      for(let k=sIdx; k<=eIdx; k++) {
                          localHighlightSet.add(k)
                          // 不同组使用不同高亮颜色
                          if (range.color) localHighlightColors.set(k, range.color)
                      }
                 }
             })
        }

        backendMatches.forEach(m => {
            let hasTimeMatch = false
            
            // 1. 时间匹配
            if (m.start_time !== undefined && m.start_time !== null) {
                 const st = m.start_time
                 const et = m.end_time
                 
                 // 找到开始的单词
                 let sIdx = wordDf.value.findIndex(w => w.end_time > st)
                 if (sIdx !== -1) {
                     // 检查是否太远？假设字幕属于该视频
                     let eIdx = sIdx
                     if (et !== undefined && et !== null) {
                         for(let i=sIdx; i<wordDf.value.length; i++) {
                             if (wordDf.value[i].start_time < et) {
                                 eIdx = i
                             } else {
                                 break
                             }
                         }
                     }
                     
                     for(let k=sIdx; k<=eIdx; k++) localTimeSet.add(k)
                     newMatches.push({ ...m, wordIdx: sIdx })
                     hasTimeMatch = true
                 }
            }
            
            // 2. 文本匹配
            if (m.text) {
                 const cleanText = m.text.toLowerCase()
                 if (cleanText) {
                     let searchPos = 0
                     while (searchPos < lowerFullText.length) {
                         const matchIdx = lowerFullText.indexOf(cleanText, searchPos)
                         if (matchIdx === -1) break
                         if (matchIdx < charToWordMap.length) {
                            const wIdx = charToWordMap[matchIdx]
                            const endCharIdx = matchIdx + cleanText.length - 1
                            const endWIdx = (endCharIdx < charToWordMap.length) ? charToWordMap[endCharIdx] : wIdx
                            
                            for(let k=wIdx; k<=endWIdx; k++) localTextSet.add(k)
                            
                            if (!hasTimeMatch) {
                                newMatches.push({ ...m, wordIdx: wIdx })
                            }
                         }
                         searchPos = matchIdx + cleanText.length
                     }
                 }
            }
            
            // 3. 高亮文本匹配
            if (m.highlight_text) {
                 // 移除 HTML 标签 (例如 <em>) 以便在原文中搜索
                 const cleanText = m.highlight_text.replace(/<[^>]*>/g, '').toLowerCase()
                 if (cleanText) {
                     let searchPos = 0
                     while (searchPos < lowerFullText.length) {
                         const matchIdx = lowerFullText.indexOf(cleanText, searchPos)
                         if (matchIdx === -1) break
                         if (matchIdx < charToWordMap.length) {
                            const wIdx = charToWordMap[matchIdx]
                            const endCharIdx = matchIdx + cleanText.length - 1
                            const endWIdx = (endCharIdx < charToWordMap.length) ? charToWordMap[endCharIdx] : wIdx
                            
                            for(let k=wIdx; k<=endWIdx; k++) localHighlightSet.add(k)
                            
                            // 如果只有 highlight_text 存在，将其视为导航点
                            if (!hasTimeMatch && !m.text) {
                                newMatches.push({ ...m, wordIdx: wIdx })
                            }
                         }
                         searchPos = matchIdx + cleanText.length
                     }
                 }
            }
        })
        
        // 按 wordIdx 排序
        newMatches.sort((a, b) => a.wordIdx - b.wordIdx)
        
        // 按 wordIdx 去重
        const uniqueMatches = []
        const seenIndices = new Set()
        newMatches.forEach(nm => {
            if (nm.wordIdx !== -1 && !seenIndices.has(nm.wordIdx)) {
                seenIndices.add(nm.wordIdx)
                uniqueMatches.push(nm)
            }
        })
        
        matches.value = uniqueMatches
        textMatchIndices.value = localTextSet
        highlightIndices.value = localHighlightSet
        highlightColorMap.value = localHighlightColors
        timeMatchIndices.value = localTimeSet
        // console.log('[SourceViewer] Matches updated:', matches.value)

        // Auto jump logic on initial load
        if (!initialJumpDone.value) {
            if (matches.value.length > 0) {
                // Jump to first match
                const firstMatch = matches.value[0]
                if (firstMatch.wordIdx !== -1) {
                    scrollToWord(firstMatch.wordIdx)
                }
            } else if (wordDf.value.length > 0) {
                // Jump to start
                scrollToWord(0)
            }
            initialJumpDone.value = true
        }
    }

    const fetchTranscript = async () => {
    //   console.log('[SourceViewer] fetchTranscript called, previewSource:', previewSource.value)
      if (!previewSource.value) return
      
      const transId = previewSource.value.transcription_id || previewSource.value.video_id || previewSource.value.id
    //   console.log('[SourceViewer] fetchTranscript transId:', transId)
      
      const isRawText = transId && (transId.length > 50 || /[\u4e00-\u9fa5]/.test(transId));

      if (!transId && !previewSource.value.transcription_text) {
            wordDf.value = []
            return
        }

        loadingTranscript.value = true
        let fetched = false
        
        try {
          if (!isRawText && transId) {
            const res = await get_video_material_info(transId)

            // 标准化不同 API 包装器的响应形状
            let payload = null
            if (res && res.data && res.data.data) payload = res.data.data
            else if (res && res.data) payload = res.data
            else payload = res

            if (payload) {
                fetched = true
                // Store metadata
                videoMetadata.value = {
                    shooting_time: payload.shooting_time,
                    duration: payload.duration
                }

                if (payload.word_df && Array.isArray(payload.word_df)) {
                wordDf.value = payload.word_df
                } else if (payload.wordDf && Array.isArray(payload.wordDf)) {
                wordDf.value = payload.wordDf
                } else if (previewSource.value.words && Array.isArray(previewSource.value.words)) {
                // Fallback to words already present in the previewSource (e.g. from disassembly or trace results)
                wordDf.value = previewSource.value.words
                } else {
                wordDf.value = []
                }

                // 如果在详细信息中找到更好的 URL，则更新视频 URL
                if ((payload.urls && payload.urls.length > 0) || (previewSource.value.is_hot_video && payload.product_name)) {
                const updatedSource = { ...previewSource.value, ...payload }
                if (payload.urls && payload.urls.length > 0) updatedSource.urls = payload.urls
                store.dispatch('multiClipper/selectPreview', updatedSource)
                }
                
                // Fetch commodities for this video - skip for hot videos
                if (!(previewSource.value && previewSource.value.is_hot_video)) {
                    fetchCommodities(payload)
                }
                
                // 更新导航匹配项
                updateMatches()
                
                // 首次加载完成，如果是带有搜索时间或商品讲解，尝试跳转
                if (previewSource.value && videoMetadata.value.shooting_time) {
                    const shootTime = new Date(videoMetadata.value.shooting_time).getTime()
                    const durationSec = videoMetadata.value.duration || 0
                    
                    // 1. 优先跳转到搜索时间
                    if (previewSource.value.search_time) {
                        const targetTime = parseUserInputTime(previewSource.value.search_time, shootTime, durationSec)
                        if (!isNaN(targetTime)) {
                            const seekTime = Math.max(0, (targetTime - shootTime) / 1000)
                            emit('request-seek', seekTime)
                        }
                    } 
                    // 2. 其次跳转到商品讲解时间
                    else if (previewSource.value['讲解开始时间']) {
                        const explainStart = new Date(previewSource.value['讲解开始时间']).getTime()
                        const relativeStartMs = Math.max(0, explainStart - shootTime)
                        emit('request-seek', relativeStartMs / 1000)
                    }
                }
            }
          }
        } catch (e) {
            console.error("Failed to fetch transcript", e)
            // 最终兜底：如果请求失败但 previewSource 本身带了 words (比如是从拆解报告跳过来的)，则使用它
            if (previewSource.value && previewSource.value.words && previewSource.value.words.length > 0) {
                wordDf.value = previewSource.value.words
            } else {
                wordDf.value = []
            }
        } finally {
            loadingTranscript.value = false
        }

        // Fallback to raw text if no wordDf from API
        if (wordDf.value.length === 0) {
            const text = isRawText ? transId : previewSource.value.transcription_text
            if (text) {
                // console.log('[SourceViewer] Using raw text fallback')
                wordDf.value = text.split('').map(char => ({ word: char }))
                // Trigger match update for text highlighting
                updateMatches()
            }
        }
        
        if (scrollAfterLoad.value) {
            nextTick(() => {
                const container = document.querySelector('.text-selection-area')
                if (container) {
                    if (scrollAfterLoad.value === 'bottom') {
                        container.scrollTop = container.scrollHeight;
                    } else {
                        container.scrollTop = 0;
                    }
                }
                scrollAfterLoad.value = null;
            })
        }
    }

    const getWordText = (word) => {
      if (!word) return ''
      if (word.word) return word.word
      if (word.text) return word.text
      if (word.words && Array.isArray(word.words)) return word.words.map(w => w.text || w.word || '').join('')
      return ''
    }

      // 监听 transcription id 或 video id 的变化（某些 payload 使用不同的字段）
    watch(() => previewSource.value, (newSource, oldSource) => {
        if (!newSource) {
            wordDf.value = []
            return
        }
        
        const newId = newSource.transcription_id || newSource.video_id || newSource.id
        const oldId = oldSource ? (oldSource.transcription_id || oldSource.video_id || oldSource.id) : null
        const tsChanged = newSource._updateTs !== (oldSource ? oldSource._updateTs : undefined)
        
        // console.log('[SourceViewer] watch triggered. newId:', newId, 'oldId:', oldId, 'tsChanged:', tsChanged)
        
        // 如果 ID 改变，或者时间戳改变（强制重新加载），或者我们还没有字幕，则获取
        if (newId !== oldId || tsChanged || (newId && wordDf.value.length === 0)) {
            selectionStartIdx.value = null
            selectionEndIdx.value = null
            currentWordIdx.value = -1
            lastSearchIdx = 0
            initialJumpDone.value = false // Reset auto jump flag
            // 如果只是刷新，不要立即清除 wordDf 以避免闪烁？
            // 实际上如果 ID 改变，最好清除。
            if (newId !== oldId) {
                wordDf.value = []
            }
            fetchTranscript()
        } else {
            // 如果 ID 没有改变（可能是相同的视频但是带着新的 matches 搜索结果或搜索时间进来的）
            // 我们只需要更新匹配的高亮
            updateMatches()
            
            if (videoMetadata.value.shooting_time) {
                const shootTime = new Date(videoMetadata.value.shooting_time).getTime()
                const durationSec = videoMetadata.value.duration || 0
                
                // 1. 优先处理搜索时间跳转
                if (newSource.search_time) {
                    const targetTime = parseUserInputTime(newSource.search_time, shootTime, durationSec)
                    if (!isNaN(targetTime)) {
                        const seekTime = Math.max(0, (targetTime - shootTime) / 1000)
                        emit('request-seek', seekTime)
                    }
                }
                // 2. 如果是同一个视频的另一个商品讲解，触发跳转
                else if (newSource['讲解开始时间']) {
                    const explainStart = new Date(newSource['讲解开始时间']).getTime()
                    const relativeStartMs = Math.max(0, explainStart - shootTime)
                    emit('request-seek', relativeStartMs / 1000)
                    
                    // 尝试同步下拉框选中状态
                    if (commodityList.value.length > 0) {
                        const matchedCommodity = commodityList.value.find(c => c['讲解开始时间'] === newSource['讲解开始时间'] && c['商品名称'] === newSource['商品名称'])
                        if (matchedCommodity) {
                            selectedCommodity.value = matchedCommodity
                        }
                    }
                }
            }
        }
    }, { deep: true })

    const updateSelectionFromNative = () => {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
             const container = document.querySelector('.transcript-content')
             
             // 如果焦点移动到了输入框或文本域，不要清除选择状态
             if (document.activeElement && 
                (document.activeElement.tagName === 'INPUT' || 
                 document.activeElement.tagName === 'TEXTAREA' ||
                 document.activeElement.isContentEditable)) {
                 return
             }

             if (container && !container.contains(sel.anchorNode)) {
                 return
             }
             
             selectionStartIdx.value = null
             selectionEndIdx.value = null
             return
        }

        const range = sel.getRangeAt(0)
        
        const getIndexFromEl = (el) => {
            if (el && el.dataset && el.dataset.index !== undefined) {
                return parseInt(el.dataset.index)
            }
            return null
        }

        const getIndex = (node) => {
            let el = node.nodeType === 3 ? node.parentNode : node
            while (el && el.dataset && el.dataset.index === undefined) {
                el = el.parentNode
                if (el && el.classList && el.classList.contains('transcript-content')) break
            }
            return getIndexFromEl(el)
        }

        const resolveIndex = (node, offset, isStart) => {
            let el = node.nodeType === 3 ? node.parentNode : node
            while (el && el.dataset && el.dataset.index === undefined) {
                 el = el.parentNode
                 if (el && el.classList && el.classList.contains('transcript-content')) break
            }
            
            if (el && el.dataset && el.dataset.index !== undefined) {
                 const idx = parseInt(el.dataset.index)
                 if (node.nodeType === 3 && node.parentNode === el) {
                      if (isStart) return (offset === node.length) ? idx + 1 : idx
                      else return (offset === 0) ? idx - 1 : idx
                 }
                 if (node === el) {
                      if (isStart) return (offset >= node.childNodes.length) ? idx + 1 : idx
                      else return (offset === 0) ? idx - 1 : idx
                 }
                 return idx
            }
            
            let container = node.nodeType === 3 ? node.parentNode : node
            if (container && container.classList && container.classList.contains('transcript-content')) {
                 if (node === container) {
                      if (offset < node.childNodes.length) {
                           let child = node.childNodes[offset]
                           let idx = getIndex(child)
                           if (idx === null) {
                               let next = child.nextSibling
                               while(next && (!next.dataset || next.dataset.index === undefined)) {
                                   next = next.nextSibling
                               }
                               if (next) {
                                   idx = parseInt(next.dataset.index)
                               }
                           }
                           if (idx !== null) return isStart ? idx : idx - 1
                           else return wordDf.value.length
                      } else {
                           return wordDf.value.length 
                      }
                 }
                 if (node.nodeType === 3 && node.parentNode === container) {
                      let next = node.nextSibling
                      while(next && (!next.dataset || next.dataset.index === undefined)) {
                          next = next.nextSibling
                      }
                      if (next) {
                          const idx = parseInt(next.dataset.index)
                          return isStart ? idx : idx - 1
                      } else {
                           return wordDf.value.length
                      }
                 }
            }
            return null
        }

        const startIdx = resolveIndex(range.startContainer, range.startOffset, true)
        const endIdx = resolveIndex(range.endContainer, range.endOffset, false)

        if (startIdx !== null && endIdx !== null) {
            const s = Math.max(0, startIdx)
            const e = Math.min(wordDf.value.length - 1, endIdx)
            
            if (s <= e) {
                selectionStartIdx.value = s
                selectionEndIdx.value = e
            } else {
                selectionStartIdx.value = null
                selectionEndIdx.value = null
            }
        } else {
             selectionStartIdx.value = null
             selectionEndIdx.value = null
        }
    }
    
    const playSelection = () => {
        setActivePanel()
        if (hasSelection.value) {
            // console.log('SourceViewer playSelection emitting request-seek:', selectionStartTime.value / 1000)
            emit('request-seek', selectionStartTime.value / 1000)
            // 注意：对于解耦的视频播放器，很难轻松播放特定时长
            // 除非我们在父组件或视频播放器中实现播放范围逻辑。
            // 目前，根据说明，仅跳转就足够了。
        }
    }

    const hasSelection = computed(() => selectionStartIdx.value !== null)

    const isWordSelected = (index) => {
        if (selectionStartIdx.value === null) return false
        const s = selectionStartIdx.value
        const e = selectionEndIdx.value !== null ? selectionEndIdx.value : s
        const start = Math.min(s, e)
        const end = Math.max(s, e)
        return index >= start && index <= end
    }
    
    const selectionStartTime = computed(() => {
        if (selectionStartIdx.value === null || !wordDf.value[selectionStartIdx.value]) return 0
        return wordDf.value[selectionStartIdx.value].start_time
    })

    const selectionEndTime = computed(() => {
        const endIdx = selectionEndIdx.value !== null ? selectionEndIdx.value : selectionStartIdx.value
        if (endIdx === null || !wordDf.value[endIdx]) return 0
        return wordDf.value[endIdx].end_time
    })

    const selectedDuration = computed(() => {
        return (selectionEndTime.value - selectionStartTime.value) / 1000
    })
    
    const updateHighlight = (time) => {
        const msTime = time * 1000
        const len = wordDf.value.length

        if (len === 0) return

        let found = -1
        let startIdx = lastSearchIdx
        if (startIdx >= len) startIdx = len - 1
        if (startIdx < 0) startIdx = 0

        if (startIdx < len && msTime < wordDf.value[startIdx].start_time - 5000) {
             startIdx = 0
        }

        for (let i = startIdx; i < len; i++) {
            const w = wordDf.value[i]
            if (msTime >= w.start_time && msTime < w.end_time) {
                found = i
                lastSearchIdx = i 
                break
            }
            if (msTime < w.start_time) {
                lastSearchIdx = Math.max(0, i - 1) 
                break 
            }
        }
        
        if (found === -1 && startIdx > 0 && msTime < wordDf.value[startIdx].start_time) {
             for (let i = startIdx - 1; i >= 0; i--) {
                const w = wordDf.value[i]
                if (msTime >= w.start_time && msTime < w.end_time) {
                    found = i
                    lastSearchIdx = i
                    break
                }
                if (msTime > w.end_time) {
                    lastSearchIdx = i
                    break
                }
             }
        }

        if (found !== currentWordIdx.value) {
            currentWordIdx.value = found
            if (autoScroll.value) {
                scrollToWord(found)
            }
        }
        
        // Auto-sync commodity dropdown
        if (!isUserSelectingCommodity.value && commodityList.value.length > 0 && videoMetadata.value.shooting_time) {
            const shootingTimeMs = new Date(videoMetadata.value.shooting_time).getTime()
            const absoluteTimeMs = shootingTimeMs + msTime
            
            let matchedCommodity = null
            for (const item of commodityList.value) {
                const explainStart = new Date(item['讲解开始时间']).getTime()
                const explainEnd = new Date(item['讲解结束时间']).getTime()
                const relativeStartMs = Math.max(0, explainStart - shootingTimeMs)
                const relativeEndMs = explainEnd - shootingTimeMs
                
                if (found !== -1 && wordDf.value[found]) {
                    const w = wordDf.value[found]
                    if (w.end_time > relativeStartMs && w.start_time < relativeEndMs) {
                        matchedCommodity = item
                        break
                    }
                } else {
                    if (absoluteTimeMs >= explainStart && absoluteTimeMs <= explainEnd) {
                        matchedCommodity = item
                        break
                    }
                }
            }
            
            if (selectedCommodity.value !== matchedCommodity) {
                selectedCommodity.value = matchedCommodity
            }
        }
    }

    watch(() => props.currentTime, (newTime) => {
        updateHighlight(newTime)
    })

    const externalSelectionRange = computed(() => store.state.multiClipper.externalSelectionRange)

     const applyExternalSelection = () => {
         const range = externalSelectionRange.value
         if (!range) return
         
         // console.log('[SourceViewer] applyExternalSelection triggered. range:', range, 'wordDf length:', wordDf.value ? wordDf.value.length : 0);

         // Ensure we have words
         if (!wordDf.value || wordDf.value.length === 0) return
         
         // Check video ID if available
         if (range.video_id) {
              const currentId = previewSource.value ? (previewSource.value.video_id || previewSource.value.id) : null
              const currentTransId = previewSource.value ? (previewSource.value.transcription_id || currentId) : null
              const targetTransId = range.transcription_id || range.video_id

              // Only apply if identifiers match
              if (currentId && String(currentId) !== String(range.video_id)) {
                  return
              }
              if (currentTransId && targetTransId && String(currentTransId) !== String(targetTransId)) {
                  return
              }
         }
         
         const startTime = Number(range.start_time)
         const endTime = Number(range.end_time)
         
         let sIdx = -1
         let eIdx = -1
         
         // Find indices
         // Assuming wordDf is sorted by time
         for (let i = 0; i < wordDf.value.length; i++) {
             const w = wordDf.value[i]
             // Logic: Find first word where end_time > startTime
             if (sIdx === -1 && w.end_time > startTime) {
                 sIdx = i
             }
             
             // Find last word where start_time < endTime
             if (w.start_time < endTime) {
                 eIdx = i
             }
         }
         
         // console.log('[SourceViewer] applyExternalSelection found indices:', sIdx, eIdx);

         if (sIdx !== -1 && eIdx !== -1 && sIdx <= eIdx) {
             selectionStartIdx.value = sIdx
             selectionEndIdx.value = eIdx
             
             // Update DOM selection with retry mechanism for async rendering
              const tryUpdateDOM = (retries = 10) => {
                  nextTick(() => {
                      const container = document.querySelector('.transcript-content')
                      if (!container) {
                          if (retries > 0) {
                              setTimeout(() => tryUpdateDOM(retries - 1), 200)
                          } else {
                              // console.log('[SourceViewer] applyExternalSelection DOM container not found after retries');
                          }
                          return
                      }
                      
                      const startEl = container.querySelector(`.word-item[data-index="${sIdx}"]`)
                      const endEl = container.querySelector(`.word-item[data-index="${eIdx}"]`)
                      
                      if (startEl && endEl) {
                          // console.log('[SourceViewer] applyExternalSelection scrolling and selecting DOM elements');
                          const sel = window.getSelection()
                          sel.removeAllRanges()
                          const r = document.createRange()
                          r.setStartBefore(startEl) 
                          r.setEndAfter(endEl)
                          sel.addRange(r)
                          
                          startEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
                      } else {
                          if (retries > 0) {
                              setTimeout(() => tryUpdateDOM(retries - 1), 1000)
                          } else {
                              // console.log('[SourceViewer] applyExternalSelection DOM elements not found for indices:', sIdx, eIdx);
                          }
                      }
                 })
             }
             tryUpdateDOM()
         }
     }

     watch(externalSelectionRange, applyExternalSelection)
     
     // Also apply when wordDf changes (e.g. after loading transcript)
     watch(wordDf, () => {
         // Use nextTick to ensure DOM is ready
         nextTick(applyExternalSelection)
     })

     const handleWordClick = (index) => {
        if (wordDf.value && wordDf.value[index]) {
            const time = wordDf.value[index].start_time / 1000
            emit('request-seek', time)
        }
    }

    const formatTime = (ms) => {
        if (!ms && ms !== 0) return '00:00'
        const s = Math.floor(ms / 1000)
        const m = Math.floor(s / 60)
        const sec = s % 60
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
    }

    const addToPool = () => {
      if (!previewSource.value) return
            const { targetPanel, autoPlay, _updateTs, ...baseSource } = previewSource.value
      
      let start_time = 0
      let end_time = (previewSource.value.duration || 10) * 1000
      let text = "Full Video (Default)"
      let selStartIdx = null
      let selEndIdx = null

      if (hasSelection.value) {
          selStartIdx = selectionStartIdx.value
          selEndIdx = selectionEndIdx.value !== null ? selectionEndIdx.value : selectionStartIdx.value

          // 向后扩展：检查紧邻的下一个词是否为短空段（≤2秒），是则包含
          const SILENCE_THRESHOLD = 2.0 // 秒
          const nextIdx = selEndIdx + 1
          if (nextIdx < wordDf.value.length) {
              const w = wordDf.value[nextIdx]
              const wText = w.word || w.text
              // 无文本内容且时长≤2秒，则扩展包含该词
              if (!wText || !wText.trim()) {
                  const dur = (Number(w.end_time) - Number(w.start_time)) / 1000
                  if (dur > 0 && dur <= SILENCE_THRESHOLD) {
                      selEndIdx = nextIdx
                  }
              }
          }

          start_time = wordDf.value[selStartIdx].start_time
          end_time = wordDf.value[selEndIdx].end_time
          
          text = wordDf.value.slice(selStartIdx, selEndIdx + 1).map(w => w.word || w.text).join('')
      }
      
      let words = []
      if (wordDf.value && wordDf.value.length > 0) {
          const sTime = start_time
          const eTime = end_time
          words = wordDf.value.filter(w => w.start_time >= sTime && w.end_time <= eTime)
      }

      // 自动检测当前片段关联的商品名称
      let autoCommodityName = null
      if (videoMetadata.value.shooting_time && commodityList.value.length > 0) {
          try {
              const shootingTimeMs = new Date(videoMetadata.value.shooting_time).getTime()
              const counts = new Map()
              commodityList.value.forEach(item => {
                  const explainStart = new Date(item['讲解开始时间']).getTime()
                  const explainEnd = new Date(item['讲解结束时间']).getTime()
                  const relStart = Math.max(0, explainStart - shootingTimeMs)
                  const relEnd = explainEnd - shootingTimeMs
                  
                  // 计算交集时长
                  const overlapStart = Math.max(start_time, relStart)
                  const overlapEnd = Math.min(end_time, relEnd)
                  
                  if (overlapEnd > overlapStart) {
                      const duration = overlapEnd - overlapStart
                      const name = item['商品名称']
                      counts.set(name, (counts.get(name) || 0) + duration)
                  }
              })
              
              if (counts.size > 0) {
                  let maxDur = -1
                  for (const [name, dur] of counts.entries()) {
                      if (dur > maxDur) {
                          maxDur = dur
                          autoCommodityName = name
                      }
                  }
              }
          } catch (e) {
              console.error('Error auto-detecting commodity for segment:', e)
          }
      }

      const segment = {
        // 先展开 previewSource 以继承属性，然后用新值覆盖
                ...baseSource,
        id: `seg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        video_id: previewSource.value.video_id || previewSource.value.id,
        video_name: previewSource.value.video_name || previewSource.value.title || previewSource.value.name || 'Untitled Video',
        video_url: (previewSource.value.urls && previewSource.value.urls[0]) || previewSource.value.video_url || previewSource.value.url,
        transcription_id: previewSource.value.transcription_id || previewSource.value.video_id || previewSource.value.id,
        duration: previewSource.value.duration,
        start_time: start_time, 
        end_time: end_time, 
        text: text,
        words: words,
        commodity_name: autoCommodityName
      }
      
      const plainSegment = JSON.parse(JSON.stringify(segment))
      
      store.dispatch('multiClipper/addToGroup', plainSegment)
    }

    const clearSelection = () => {
        selectionStartIdx.value = null
        selectionEndIdx.value = null
    }

    const handleKeyDown = (event) => {
        // 空格键：由于视频不在这里，已将其移除？
        // 或者我们应该发出 'toggle-play' 事件？
        // 用户要求提取视频组件，键盘控制可能是全局的或在视频播放器上。
        // 但是如果用户聚焦在 SourceViewer 上，空格键应该切换视频。
        // 让我们保留它，但需要分发全局事件或类似事件。
        // 目前，依赖 VideoPlayer 焦点或仅在此处删除空格键控制以避免冲突更简单。
        
        // 'A' 键：添加到剪辑池
        if (event.code === 'KeyA') {
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
            if (hasSelection.value) {
                event.preventDefault();
                addToPool();
            }
        }
    }

    onMounted(() => {
        // console.log('[SourceViewer] onMounted check. previewSource:', !!previewSource.value)
        if (previewSource.value && wordDf.value.length === 0) {
            fetchTranscript()
        }
        window.addEventListener('keydown', handleKeyDown)
        document.addEventListener('selectionchange', updateSelectionFromNative)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('selectionchange', updateSelectionFromNative)
        // Clear wordDf to help GC when unmounting
        wordDf.value = []
        matches.value = []
        textMatchIndices.value.clear()
        highlightIndices.value.clear()
        highlightColorMap.value.clear()
        timeMatchIndices.value.clear()
    })

    const selectWord = (index) => {
        // ... implementation needed? No, just exported for template
    }

    // Return all necessary refs and methods
    return {
      showHelp,
      previewSource,
      wordDf,
      loadingTranscript,
      selectionStartIdx,
      selectionEndIdx,
      currentWordIdx,
      matches,
      currentMatchIdx,
      currentMatchDisplayIndex,
      textMatchIndices,
      highlightIndices,
      highlightColorMap,
      timeMatchIndices,
      wordCommodityColors,
      videoMetadata,
      currentDisplayTime,
      editableTimeStr,
      isEditingTime,
      timeInput,
      startEditingTime,
      handleTimeSeek,
      resetTimeDisplay,
      
      commodityList,
      selectedCommodity,
      loadingCommodities,
      isDropdownOpen,
      selectCommodityItem,
      selectCommoditySegment,
      handleCommodityChange,

      handleContainerClick,
      handleWordClick,
      jumpToPrevMatch,
      jumpToNextMatch,
      handleLocate,
      handleUserInteraction,
      handleNavigate,
      addToPool,
      
      influencerName,
      formatDateTimeStr,

      getWordText,
      getWordStyle,
      getCommodityColor,
      isWordSelected,
      hasSelection,
      selectionStartTime,
      selectionEndTime,
      selectedDuration
    }
  }
}
</script>

<style scoped>
.source-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #ddd;
  position: relative;
}

.viewer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.info-area {
  padding: 10px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
}

.tag-influencer {
  background-color: #E3F2FD;
  color: #1565C0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.top-control-row {
  margin-bottom: 4px;
}

.influencer-container {
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;
}

.actions-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 5px 0;
}

.control-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.nav-controls {
    display: flex;
    align-items: center;
    gap: 2px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 2px;
}

.right-controls {
    display: flex;
    align-items: center;
    gap: 4px;
}

.time-display {
    font-family: monospace;
    font-size: 13px;
    color: #333;
    padding: 4px 8px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-width: 140px;
    text-align: center;
    user-select: none;
}

.editable-time:hover {
    background-color: #f0f8ff;
    border-color: #1976D2;
}

.time-display-input {
    font-family: monospace;
    font-size: 13px;
    color: #333;
    padding: 4px 8px;
    background: #fff;
    border: 1px solid #1976D2;
    border-radius: 4px;
    min-width: 140px;
    width: 140px;
    text-align: center;
    outline: none;
    box-sizing: border-box;
}

.help-btn-inline {
  color: #888;
  font-size: 16px;
  padding: 0 2px;
  min-width: unset;
}

.help-btn-inline:hover {
  color: #1976D2;
  background-color: transparent;
}

.locate-btn {
    border: 1px solid #ddd !important;
    border-radius: 4px;
    background: #fff !important;
}

.locate-btn:hover {
    background: #f0f0f0 !important;
}

.nav-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    color: #555;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nav-btn:hover {
    background-color: #f0f0f0;
    color: #1976D2;
}

.match-counter {
    font-size: 12px;
    color: #666;
    padding: 0 4px;
    min-width: 30px;
    text-align: center;
}

.product-placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
}

.hot-product-info {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    background: #FFF3E0;
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid #FFE0B2;
    max-width: 400px;
}

.product-label {
    color: #E65100;
    font-weight: bold;
    white-space: nowrap;
}

.product-value {
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.color-swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    margin-right: 4px;
    margin-bottom: -1px;
    flex-shrink: 0;
    border: 1px solid #ddd;
}

.commodity-select {
    width: 100%;
    max-width: 280px;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
    color: #333;
    background-color: #fff;
    cursor: pointer;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.commodity-select:focus {
    outline: none;
    border-color: #1976D2;
}

.hint-text {
    font-size: 13px;
    color: #888;
}

.add-btn {
    padding: 4px 12px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9em;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 100px;
    min-height: 36px;
}

.btn-main-text {
    line-height: 1.2;
}

.btn-sub-text {
    font-size: 0.8em;
    opacity: 0.9;
    line-height: 1.2;
}

.add-btn:disabled {
    background-color: #A5D6A7;
    cursor: not-allowed;
}

.text-selection-area {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  line-height: 1.8;
  position: relative;
}

.video-nav-inline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #f9f9f9;
  border-bottom: 1px solid #eee;
  border-top: 1px solid #eee;
  margin: 10px 0;
  border-radius: 4px;
}

.video-nav-inline .nav-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  transition: color 0.2s;
  user-select: none;
}

.video-nav-inline .nav-item:hover {
  color: #1976D2;
}

.video-nav-inline .nav-item i {
  font-size: 18px;
  margin: 0 4px;
}

.video-nav-inline .nav-info {
  font-size: 12px;
  color: #999;
}

.transcript-content {
    white-space: pre-wrap;
    word-break: break-all;
}

.transcript-content .word-item {
    cursor: text !important;
}

.word-item {
    cursor: pointer;
    padding: 2px 0;
    border-radius: 2px;
    transition: background-color 0.2s;
}

.word-item:hover {
    background-color: #f0f0f0;
}

.word-item.user-selected {
    background-color: #bbdefb !important; /* Blue 100 */
    border-radius: 0;
}

.word-item.playing {
    color: #E53935;
    background-color: #FFEBEE;
    font-weight: bold;
}

/* Help Modal Styles */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-modal {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.help-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.help-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.close-btn:hover {
  color: #333;
}

.help-content {
  padding: 20px;
  overflow-y: auto;
}

.help-step {
  display: flex;
  margin-bottom: 20px;
}

.help-step:last-child {
  margin-bottom: 0;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e3f2fd;
  color: #1976d2;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 20px;
}

.step-info h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #333;
}

.step-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.help-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.primary-btn {
  background: #2196f3;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.primary-btn:hover {
  background: #1976d2;
}

.help-btn {
  border: 1px solid #ddd !important;
  border-radius: 4px;
  background: #fff !important;
  margin-left: 4px;
}

.help-btn:hover {
  background: #f0f0f0 !important;
  color: #1976d2;
}

.word-item.matched-text {
    background-color: #FFF9C4; /* Light Yellow */
}

.word-item.matched-highlight {
    background-color: #FFECB3; /* Amber */
    border-bottom: 2px solid #FFC107;
}

.word-item.matched-time {
    background-color: #E3F2FD; /* Light Blue */
}

/* Priority handling: Highlight > Text > Time */
.word-item.matched-highlight.matched-text {
    background-color: #FFECB3;
}

/* Selected range styling */
/* Note: Since we don't have a class for selection range yet, we might need logic for it. 
   But user didn't ask for selection styling change specifically. */

.loading-hint, .transcript-hint {
    text-align: center;
    color: #999;
    margin-top: 50px;
}

.placeholder-full {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
}

.empty-state {
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 10px;
}

.custom-select-container {
    position: relative;
    width: 100%;
    max-width: 280px;
}

.custom-select-trigger {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    min-height: 42px;
}

.selected-item-display, .custom-option {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.item-name {
    font-weight: 500;
    color: #333;
    word-break: break-word;
    white-space: normal;
}

.item-details {
    font-size: 11px;
    color: #888;
}

.custom-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: 250px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    z-index: 100;
    margin-top: 4px;
}

.custom-option {
    padding: 6px 8px;
    cursor: pointer;
    border-bottom: 1px solid #f5f5f5;
}

.custom-option:hover {
    background-color: #f0f8ff;
}

.custom-option.active {
    background-color: #e3f2fd;
}

.dropdown-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 99;
}

.select-segment-btn {
    padding: 4px 10px;
    background-color: #1976D2;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 110px;
    min-height: 36px;
    font-size: 12px;
}
.select-segment-btn:hover {
    background-color: #1565C0;
}
</style>
